import { useState, useEffect } from 'react'
import { Formik } from 'formik'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AlertDialog from 'components/AlertDialog'
import splitScheduleValidator from 'validators/splitScheduleValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaColumns } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Form,
  Row,
  BottomRow,
  FormField,
  Input,
  SubTitle,
  Label,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

/*
  Intervalo máximo, em minutos, entre os horários dos dois agendamentos resultantes.
  É o mesmo intervalo aplicado pela API em `ScheduleService.HAND_AND_FOOT_TIME_RANGE` e pelas
  sugestões de horário de `TimeAlternativesHandler.getNearbyTimes`.
*/
const TIME_RANGE_IN_MINUTES = 60

export default function SplitScheduleForm ({
  data: schedule,
  onSplit: handleSplit = () => {}
}) {
  const { id: schedule_id, professional, service, date, time } = schedule

  const [visible, setVisible] = useState (false)
  const [loading, setLoading] = useState (true)
  const [submitting, setSubmitting] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [professionals, setProfessionals] = useState ([])
  const [serviceOptions, setServiceOptions] = useState ([])
  const [timeWindows, setTimeWindows] = useState ([])
  const [valuesToConfirm, setValuesToConfirm] = useState (null)

  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const hasEditPermission = AuthService.hasPermission ('agenda', 'edit')
  const isHandAndFootService = service?.name?.toLowerCase () === 'pé e mão'

  /*
    Um agendamento já dividido chega com `scheduleTwo` preenchido pela API, e um que já está em
    uma comanda teria o valor dela dessincronizado pela divisão. A comparação com `businessId`
    deixa o formulário de fora das telas em que se navega por agendamentos sem estar dentro do
    salão dono deles, como a lista de agendamentos de um membro no painel da Cutilei.
  */
  const canSplit = hasEditPermission && schedule.cutilei_schedule && isHandAndFootService
                && schedule.active && !schedule.canceled && !schedule.scheduleTwo && !schedule.bill_id
                && !!businessId && schedule.business?.id === businessId

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    setVisible (false)
    setErrorMessage (null)
  }, [schedule_id])

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    try {
      setLoading (true)
      setErrorMessage (null)

      const [{ data: services }, { data: professionals }] = await Promise.all ([
        cutileiApi.get (
          `/businesses/${businessId}/services?status=active&cutilei_services=true`, requestConfig
        ),
        cutileiApi.get (
          `/businesses/${businessId}/professionals?status=active&deleted=false&access_level=service`
          + `&category_name=${encodeURIComponent (service.category.name)}`,
          requestConfig
        )
      ])

      const splitServices = ['mão', 'pé']
        .map (name => services.find (service => service.name.toLowerCase () === name))
        .filter (service => !!service)

      setServiceOptions (splitServices.map (service => ({
        label: service.name, value: service
      })))

      setProfessionals (professionals
        .filter (({ id }) => id !== professional.id)
        .map (({ id, nickname }) => ({ label: nickname, value: id }))
      )
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    } finally {
      setLoading (false)
    }
  }

  const getTimeWindows = async (professionalId, serviceId) => {
    try {
      const { data: timeWindows } = await cutileiApi.get (
        `/professionals/${professionalId}/free_schedules?date=${date}&business_id=${businessId}`
        + `&service_id=${serviceId}&fallback_to_business_service=true`,
        requestConfig
      )

      const options = filterNearbyTimeWindows (timeWindows).map (timeWindow => ({
        label: timeWindow, value: timeWindow
      }))

      setTimeWindows (options)
      return options
    } catch (error) {
      logApiError (error)
      setTimeWindows ([])
      return []
    }
  }

  const filterNearbyTimeWindows = timeWindows => {
    const scheduleTime = DateTime.fromFormat (time, 'H:mm')

    return timeWindows.filter (timeWindow => Math.abs (
      DateTime.fromFormat (timeWindow, 'H:mm').diff (scheduleTime, 'minutes').minutes
    ) <= TIME_RANGE_IN_MINUTES)
  }

  const splitSchedule = async values => {
    setErrorMessage (null)
    setSubmitting (true)

    try {
      const { data: schedules } = await cutileiApi.post (
        `/schedules/${schedule_id}/split`, {
          serviceId: values.service.value.id,
          professionalTwoId: values.professionalTwo.value,
          serviceTwoId: values.serviceTwo.value.id,
          timeTwo: values.timeTwo.value
        }, requestConfig
      )

      setVisible (false)
      handleSplit (schedules)
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    } finally {
      setSubmitting (false)
    }
  }

  const toggleForm = () => {
    if (!visible) setLoading (true)
    setVisible (!visible)
  }

  if (!canSplit) return null

  return (
    <Container>
      <BottomRow>
        <SecondaryButton
          label={visible ? 'Cancelar divisão' : 'Dividir em Pé e Mão'}
          leftIcon={<FaColumns size={12} color={visible ? '#FFFFFF' : '#FFC100'}/>}
          onClick={toggleForm}
          color={visible ? '#FF3939' : '#606060'}
          style={{width: 210}}
        />
      </BottomRow>
      {visible && (loading ? (
        <Loading style={{marginBlock: 20, alignSelf: 'center'}}/>
      ) : serviceOptions.length < 2 ? (
        <ErrorContainer>
          <InfoText>
            {errorMessage ?? 'Este salão não possui os serviços de "Mão" e "Pé" cadastrados'}
          </InfoText>
        </ErrorContainer>
      ) : (
        <Formik
          validationSchema={splitScheduleValidator}
          initialValues={{
            'service': serviceOptions[0],
            'professionalTwo': null,
            'serviceTwo': serviceOptions[1],
            'timeTwo': null
          }}
          onSubmit={(values, { setSubmitting: setFormikSubmitting }) => {
            setValuesToConfirm (values)
            setFormikSubmitting (false)
          }}
        >
          {function SplitScheduleForm ({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            handleSubmit
          }) {
            useEffect (() => {
              const { professionalTwo, serviceTwo } = values

              if (!professionalTwo || !serviceTwo) return setTimeWindows ([])

              getTimeWindows (professionalTwo.value, serviceTwo.value.id).then (options => {
                /*
                  O horário original vem pré-selecionado sempre que estiver livre: com os dois
                  agendamentos no mesmo horário, a API continua reconhecendo o par e validação,
                  cancelamento e exibição na agenda seguem agindo em conjunto.
                */
                setFieldValue ('timeTwo', options.find (({ value }) => value === time) ?? null)
              })
            }, [values.professionalTwo, values.serviceTwo])

            /*
              Os dois serviços são sempre um "Mão" e um "Pé": trocar o de um dos profissionais
              move o outro serviço para o profissional oposto.
            */
            const handleServiceChange = (name, option) => {
              const oppositeField = name === 'service' ? 'serviceTwo' : 'service'
              const oppositeOption = serviceOptions.find (({ value }) => value.id !== option.value.id)

              setFieldValue (name, option)
              setFieldValue (oppositeField, oppositeOption ?? null)
            }

            return (
              <Form>
                <SubTitle>Divisão do agendamento</SubTitle>
                <Row>
                  <FormField>
                    <Label>Profissional</Label>
                    <Input type='text' value={professional.nickname} disabled/>
                  </FormField>
                  <FormField>
                    <Label>Serviço</Label>
                    <Select
                      name='service'
                      placeholder='Serviço...'
                      value={values.service}
                      options={serviceOptions}
                      onChange={handleServiceChange}
                      onBlur={setFieldTouched}
                      error={errors.service}
                      touched={touched.service}
                    />
                    {touched.service && errors.service && (
                      <WarningText>
                        {errors.service}
                      </WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <Label>Horário</Label>
                    <Input type='text' value={time} disabled/>
                  </FormField>
                </Row>
                <Row>
                  <FormField>
                    <Label>Profissional</Label>
                    <Select
                      name='professionalTwo'
                      placeholder='Profissional...'
                      noOptionsMessage='Nenhum outro profissional atende a esta categoria'
                      value={values.professionalTwo}
                      options={professionals}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      error={errors.professionalTwo}
                      touched={touched.professionalTwo}
                    />
                    {touched.professionalTwo && errors.professionalTwo && (
                      <WarningText>
                        {errors.professionalTwo}
                      </WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <Label>Serviço</Label>
                    <Select
                      name='serviceTwo'
                      placeholder='Serviço...'
                      value={values.serviceTwo}
                      options={serviceOptions}
                      onChange={handleServiceChange}
                      onBlur={setFieldTouched}
                      error={errors.serviceTwo}
                      touched={touched.serviceTwo}
                    />
                    {touched.serviceTwo && errors.serviceTwo && (
                      <WarningText>
                        {errors.serviceTwo}
                      </WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <Label>Horário</Label>
                    <Select
                      name='timeTwo'
                      placeholder='Horário...'
                      noOptionsMessage='Nenhum horário encontrado'
                      value={values.timeTwo}
                      options={timeWindows}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      error={errors.timeTwo}
                      touched={touched.timeTwo}
                      isDisabled={!values.professionalTwo}
                    />
                    {touched.timeTwo && errors.timeTwo && (
                      <WarningText>
                        {errors.timeTwo}
                      </WarningText>
                    )}
                  </FormField>
                </Row>
                {errorMessage && (
                  <ErrorContainer>
                    <InfoText>{errorMessage}</InfoText>
                  </ErrorContainer>
                )}
                <MainButton
                  label='Dividir agendamento'
                  onClick={handleSubmit}
                  disabled={submitting}
                  loading={submitting}
                  width={180}
                />
              </Form>
            )
          }}
        </Formik>
      ))}
      <AlertDialog
        visible={!!valuesToConfirm}
        title='Atenção!'
        message={valuesToConfirm && (
          <>
            {`Deseja realmente dividir este agendamento? `}
            <b>{professional.nickname}</b>
            {` fará `}
            <b>{valuesToConfirm.service.label}</b>
            {` às ${time}, e `}
            <b>{valuesToConfirm.professionalTwo.label}</b>
            {` fará `}
            <b>{valuesToConfirm.serviceTwo.label}</b>
            {` às ${valuesToConfirm.timeTwo.value}.`}
          </>
        )}
        confirmText='Sim'
        cancelText='Não'
        onConfirm={() => splitSchedule (valuesToConfirm)}
        onClose={() => setValuesToConfirm (null)}
      />
    </Container>
  )
}
